// hp-acquire-card.js — Acquire section: expandable cards on desktop, Swiper on mobile

const DURATION = 0.4;
const EASE = 'power3.inOut';

// ─────────────────────────────────────────────
// SHARED: image sync (used by both desktop + mobile)
// ─────────────────────────────────────────────
function syncImage(cardValue) {
  const images = document.querySelectorAll('[acuire-card-img]');
  images.forEach((img) => {
    const isMatch = img.getAttribute('acuire-card-img') === cardValue;
    gsap.to(img, { opacity: isMatch ? 1 : 0, duration: DURATION, ease: EASE });
  });
}

// ─────────────────────────────────────────────
// DESKTOP: expandable card + image sync
// ─────────────────────────────────────────────
function initDesktop() {
  const cards = document.querySelectorAll('[acuire-card]');
  const images = document.querySelectorAll('[acuire-card-img]');
  if (!cards.length) return;

  const COLOR = {
    activeBg: '#ffffff',
    inactiveBg: '#A0A7F5',
    activeBorder: '#7979E4',
    inactiveBorder: 'transparent',
  };

  const getContent = (card) => card.querySelector('.hp-acquire-card-rich-text, p, ul');

  function collapse(card) {
    const content = getContent(card);
    card.classList.remove('is--active');
    gsap.to(card, {
      backgroundColor: COLOR.inactiveBg,
      borderColor: COLOR.inactiveBorder,
      duration: DURATION,
      ease: EASE,
    });
    if (content) {
      gsap.set(content, { overflow: 'hidden' });
      gsap.to(content, {
        height: 0,
        opacity: 0,
        marginTop: 0,
        duration: DURATION,
        ease: EASE,
        overwrite: true,
      });
    }
  }

  function expand(card) {
    const content = getContent(card);
    card.classList.add('is--active');
    gsap.to(card, {
      backgroundColor: COLOR.activeBg,
      borderColor: COLOR.activeBorder,
      duration: DURATION,
      ease: EASE,
    });
    if (content) {
      gsap.set(content, { height: 'auto', opacity: 1 });
      const naturalHeight = content.offsetHeight;
      gsap.fromTo(
        content,
        { height: 0, opacity: 0, marginTop: 0 },
        {
          height: naturalHeight,
          opacity: 1,
          marginTop: '0.75rem',
          duration: DURATION,
          ease: EASE,
          overwrite: true,
          onComplete: () => gsap.set(content, { height: 'auto', overflow: 'visible' }),
        }
      );
    }
  }

  function lockContainerHeight() {
    const flex = document.querySelector('.hp-acquire_top-left-flex');
    if (!flex) return;
    cards.forEach((card) => {
      const content = getContent(card);
      if (content) gsap.set(content, { height: 'auto', opacity: 1, marginTop: '0.75rem' });
    });
    const tallest = flex.offsetHeight;
    flex.style.minHeight = `${tallest}px`;
    cards.forEach((card) => {
      const content = getContent(card);
      if (content) gsap.set(content, { height: 0, opacity: 0, marginTop: 0, overflow: 'hidden' });
    });
  }

  lockContainerHeight();

  cards.forEach((card) => {
    const content = getContent(card);
    if (content) gsap.set(content, { height: 0, opacity: 0, overflow: 'hidden' });
  });

  const firstValue = cards[0].getAttribute('acuire-card');
  expand(cards[0]);
  syncImage(firstValue);

  images.forEach((img) => {
    if (img.getAttribute('acuire-card-img') !== firstValue) gsap.set(img, { opacity: 0 });
  });

  const onClick = function () {
    if (this.classList.contains('is--active')) return;
    const value = this.getAttribute('acuire-card');
    cards.forEach((c) => {
      if (c !== this) collapse(c);
    });
    expand(this);
    syncImage(value);
  };

  cards.forEach((card) => card.addEventListener('click', onClick));

  // Teardown
  return () => {
    cards.forEach((card) => card.removeEventListener('click', onClick));
    cards.forEach((card) => {
      card.classList.remove('is--active');
      const content = getContent(card);
      if (content) gsap.set(content, { clearProps: 'all' });
      gsap.set(card, { clearProps: 'all' });
    });
    images.forEach((img) => gsap.set(img, { clearProps: 'all' }));
    const flex = document.querySelector('.hp-acquire_top-left-flex');
    if (flex) flex.style.minHeight = '';
  };
}

// ─────────────────────────────────────────────
// MOBILE: swiper + image sync on slide change
// ─────────────────────────────────────────────
function initMobile() {
  const swiperGroup = document.querySelector("[data-swiper-group='is--acquire']");
  if (!swiperGroup) return;

  const swiperSliderWrap = swiperGroup.querySelector("[data-swiper-wrap='is--acquire']");
  if (!swiperSliderWrap) return;

  const prevButton = swiperGroup.querySelector("[data-swiper-prev='is--acquire']");
  const nextButton = swiperGroup.querySelector("[data-swiper-next='is--acquire']");
  const paginationEl = swiperGroup.querySelector("[data-swiper-pagination='is--acquire']");
  const images = document.querySelectorAll('[acuire-card-img]');

  // Reads the acuire-card value from the currently active slide's inner card
  const getActiveCardValue = (swiper) => {
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return null;
    const card = activeSlide.querySelector('[acuire-card]');
    return card ? card.getAttribute('acuire-card') : null;
  };

  const swiperInstance = new Swiper(swiperSliderWrap, {
    slidesPerView: 'auto',
    speed: 600,
    grabCursor: true,
    navigation: { nextEl: nextButton, prevEl: prevButton },
    pagination: { el: paginationEl, type: 'bullets', clickable: true },
    keyboard: { enabled: true, onlyInViewport: false },
    on: {
      init(swiper) {
        const value = getActiveCardValue(swiper);
        if (value) syncImage(value);
      },
      slideChange(swiper) {
        const value = getActiveCardValue(swiper);
        if (value) syncImage(value);
      },
    },
  });

  // Teardown
  return () => {
    try {
      swiperInstance.destroy(true, true);
    } catch (e) {}
    // Reset image opacities so desktop init starts clean
    images.forEach((img) => gsap.set(img, { clearProps: 'all' }));
  };
}

// ─────────────────────────────────────────────
// ORCHESTRATOR
// ─────────────────────────────────────────────
export function initAcquireCard() {
  let teardown = null;
  let lastMode = null;

  function setup() {
    const mode = window.innerWidth >= 992 ? 'desktop' : 'mobile';
    if (mode === lastMode) return;

    if (teardown) {
      teardown();
      teardown = null;
    }

    teardown = mode === 'desktop' ? initDesktop() : initMobile();
    lastMode = mode;
  }

  setup();

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(setup, 200);
  });
}
